/**
 * מיזוג תשובת orderByID (Gimmonix / שורת הזמנה) ל־currentHotelOrder עבור תשלום / searchoffer hydrate.
 */

export function parseOrderByIdBody(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
}

export function safeJson(v, fallback = null) {
  if (v == null) return fallback;
  if (typeof v === "object" && !Array.isArray(v)) return v;
  if (typeof v === "string") {
    try {
      const o = JSON.parse(v);
      return o && typeof o === "object" ? o : fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

function buildOfferFromOrderRow(row) {
  const our = safeJson(row.ourprice, {}) || {};
  const pd =
    safeJson(row.packagepricedetails, null) ||
    (typeof row.packagepricedetails === "object" ? row.packagepricedetails : {}) ||
    {};
  let pwc =
    parseFloat(our.priceWithCommission) ||
    parseFloat(our.priceWithCreditCardFee) ||
    parseFloat(pd.FinalPrice) ||
    parseFloat(row.pricetotal) ||
    parseFloat(row.price) ||
    parseFloat(row.bookingprice) ||
    parseFloat(row.totalpricenow) ||
    parseFloat(row.totalprice) ||
    0;
  if (!Number.isFinite(pwc) || pwc <= 0) pwc = 0.01;
  const hfType = String(row.handle_fee_option || "+").trim() === "%" ? "%" : "+";
  const hfVal = parseFloat(row.handlingfee) || 0;
  const pkgId = row.packageid ?? row.hotelpackageid ?? row.hotelorderid ?? 0;
  let passangerData = safeJson(row.passangerdata, null) || safeJson(row.passengerinfo, null);
  if (!passangerData || typeof passangerData !== "object") {
    passangerData = { passengerInfo: [] };
  } else if (!passangerData.passengerInfo) {
    passangerData = {
      passengerInfo: Array.isArray(passangerData) ? passangerData : [],
    };
  }
  return {
    selectedHotelPackage: {
      packageID: pkgId,
      ourPrice: {
        ...our,
        priceWithCommission: pwc,
        handleFeeData: { type: hfType, value: hfVal },
      },
    },
    selectedHotel: { Result: [] },
    clientsInfo: safeJson(row.clientsinfo, {}) || {},
    passangerData,
    searchPhrase: safeJson(row.searchphrase, null) || row.searchphrase || null,
  };
}

/** IDs that may appear on a hydrated offer / order row for route matching. */
export function collectOrderRouteMatchIds(order) {
  if (!order || typeof order !== "object") return [];
  return [
    order._hydratedRouteOrderId,
    order.id,
    order.orderId,
    order.order_id,
    order.dealid,
    order.dealId,
    order.selectedHotelPackage?.packageID,
    order.selectedHotelPackage?.PackageId,
    order.selectedHotelPackage?.packageId,
  ].filter((v) => v != null && String(v).trim() !== "");
}

/**
 * True when the in-memory offer belongs to the searchoffer/:selectedPackageId route.
 * Package-only LDS links use the order/proposal UUID in the URL, which often differs
 * from selectedHotelPackage.packageID — matching only packageID caused a hydrate→clear loop.
 */
export function orderMatchesSearchOfferRouteId(order, routeId) {
  if (!order || routeId == null || String(routeId).trim() === "") return false;
  const target = String(routeId).trim();
  return collectOrderRouteMatchIds(order).some((id) => String(id).trim() === target);
}

export function isSearchOfferHydrateUsable(order) {
  if (!order || typeof order !== "object") return false;
  const hotel = order.selectedHotel;
  if (hotel && typeof hotel === "object") {
    const name =
      hotel.HotelName ||
      hotel.hotelName ||
      hotel.Name ||
      hotel.name ||
      null;
    const hotelId =
      hotel.HotelId ||
      hotel.hotelId ||
      hotel.hotelID ||
      hotel.id ||
      null;
    if (name != null && String(name).trim() !== "") return true;
    if (hotelId != null && String(hotelId).trim() !== "" && String(hotelId) !== "0") {
      return true;
    }
    // Non-empty Result[] from supplier payloads
    if (Array.isArray(hotel.Result) && hotel.Result.length > 0) return true;
  }
  if (order.hotel && typeof order.hotel === "object") return true;
  const pkg = order.selectedHotelPackage;
  if (pkg && typeof pkg === "object") {
    if (Array.isArray(pkg.roomNames) && pkg.roomNames.length > 0) return true;
    if (Array.isArray(pkg.rooms) && pkg.rooms.length > 0) return true;
  }
  return false;
}

export function isOrderByIdResponseFail(data) {
  if (data == null) return true;
  if (typeof data !== "object" || Array.isArray(data)) return true;
  const status = data.status != null ? String(data.status).trim().toLowerCase() : "";
  if (status === "fail" || status === "error" || status === "perms") return true;
  if (typeof data.error === "string" && data.error.trim() !== "") return true;
  const msg = data.message != null ? String(data.message) : "";
  if (/not found|unauthorized|missing required/i.test(msg)) return true;
  return false;
}

export function mergeOfferFromOrder(orderData) {
  let offer = orderData.offer;
  offer = safeJson(offer, offer);
  const built = buildOfferFromOrderRow(orderData);
  const orderRowId =
    orderData?.id ??
    orderData?.orderId ??
    orderData?.order_id ??
    orderData?.dealid ??
    orderData?.dealId ??
    null;
  if (!offer || typeof offer !== "object" || Array.isArray(offer)) {
    return orderRowId != null ? { ...built, id: orderRowId } : built;
  }
  return {
    ...built,
    ...offer,
    ...(orderRowId != null ? { id: offer.id ?? orderRowId } : {}),
    selectedHotelPackage: {
      ...built.selectedHotelPackage,
      ...(offer.selectedHotelPackage || {}),
      ourPrice: {
        ...built.selectedHotelPackage.ourPrice,
        ...(offer.selectedHotelPackage?.ourPrice || {}),
        priceWithCommission:
          parseFloat(offer.selectedHotelPackage?.ourPrice?.priceWithCommission) ||
          built.selectedHotelPackage.ourPrice.priceWithCommission,
        handleFeeData:
          offer.selectedHotelPackage?.ourPrice?.handleFeeData ||
          built.selectedHotelPackage.ourPrice.handleFeeData,
      },
    },
    passangerData: offer.passangerData || built.passangerData,
    clientsInfo: { ...built.clientsInfo, ...(offer.clientsInfo || {}) },
  };
}
